import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CompanyRegisterPage from './pages/CompanyRegisterPage';
import DashboardPage from './pages/DashboardPage';
import ProductsPage from './pages/ProductsPage';
import GenerateQRPage from './pages/GenerateQRPage';
import ReportsPage from './pages/ReportsPage';
import QRStatsPage from './pages/QRStatsPage';
import UsersPage from './pages/UsersPage';
import RolesPage from './pages/RolesPage';
import DepartmentsPage from './pages/DepartmentsPage';
import PermissionsPage from './pages/PermissionsPage';
import VerifyPage from './pages/VerifyPage';
import FieldScanPage from './pages/FieldScanPage';
import QRCodesPage from './pages/QRCodesPage';
import MasterQRDetailPage from './pages/MasterQRDetailPage';
import ReprintRequestsPage from './pages/ReprintRequestsPage';
import ProfilePage from './pages/ProfilePage';
import OrganizationSettingsPage from './pages/OrganizationSettingsPage';
import AuditLogPage from './pages/AuditLogPage';
import WorkflowTemplatesPage from './pages/WorkflowTemplatesPage';
import WorkflowDesignerPage from './pages/WorkflowDesignerPage';
import SimpleWorkflowDesignerPage from './pages/SimpleWorkflowDesignerPage';
import PricingPage from './pages/PricingPage';
import PaymentPage from './pages/PaymentPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MobileUserRegistrationPage from './pages/MobileUserRegistrationPage';
import SuperUserLoginPage from './pages/SuperUserLoginPage';
import SuperUserDashboardPage from './pages/SuperUserDashboardPage';
import ContractPrintersPage from './pages/ContractPrintersPage';
import PrintJobsPage from './pages/PrintJobsPage';

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full" />
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user || user === false) return <Navigate to="/login" replace />;
  return children;
}

// Public Route Component (redirect to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (user && user !== false) return <Navigate to="/dashboard" replace />;
  return children;
}

// Home: pricing landing for visitors, dashboard for signed-in users
function HomeRedirect() {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (user && user !== false) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/pricing" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public auth */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/register/company" element={<PublicRoute><CompanyRegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/login/mobile-registration" element={<MobileUserRegistrationPage />} />

      {/* Super user */}
      <Route path="/super-user/login" element={<SuperUserLoginPage />} />
      <Route path="/super-user/dashboard" element={<SuperUserDashboardPage />} />

      {/* Public QR Verification */}
      <Route path="/verify/:qrId" element={<VerifyPage />} />

      {/* Public pricing & billing */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/payment" element={<PaymentPage />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
      <Route path="/generate-qr" element={<ProtectedRoute><GenerateQRPage /></ProtectedRoute>} />
      <Route path="/field-scan" element={<ProtectedRoute><FieldScanPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/qr-stats" element={<ProtectedRoute><QRStatsPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/roles" element={<ProtectedRoute><RolesPage /></ProtectedRoute>} />
      <Route path="/departments" element={<ProtectedRoute><DepartmentsPage /></ProtectedRoute>} />
      <Route path="/permissions" element={<ProtectedRoute><PermissionsPage /></ProtectedRoute>} />
      <Route path="/qr-codes" element={<ProtectedRoute><QRCodesPage /></ProtectedRoute>} />
      <Route path="/qr-codes/:masterQrId" element={<ProtectedRoute><MasterQRDetailPage /></ProtectedRoute>} />
      <Route path="/reprint-requests" element={<ProtectedRoute><ReprintRequestsPage /></ProtectedRoute>} />
      <Route path="/contract-printers" element={<ProtectedRoute><ContractPrintersPage /></ProtectedRoute>} />
      <Route path="/print-jobs" element={<ProtectedRoute><PrintJobsPage /></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/organization" element={<ProtectedRoute><OrganizationSettingsPage /></ProtectedRoute>} />
      <Route path="/workflows" element={<ProtectedRoute><WorkflowTemplatesPage /></ProtectedRoute>} />
      <Route path="/workflows/canvas/new" element={<ProtectedRoute><WorkflowDesignerPage /></ProtectedRoute>} />
      <Route path="/workflows/canvas/:templateId" element={<ProtectedRoute><WorkflowDesignerPage /></ProtectedRoute>} />
      <Route path="/workflows/new" element={<ProtectedRoute><SimpleWorkflowDesignerPage /></ProtectedRoute>} />
      <Route path="/workflows/:templateId" element={<ProtectedRoute><SimpleWorkflowDesignerPage /></ProtectedRoute>} />

      <Route path="/" element={<HomeRedirect />} />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

function ToasterWithTheme() {
  const { theme } = useTheme();
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        style: {
          background: theme === 'dark' ? '#1f1f23' : '#ffffff',
          border: theme === 'dark' ? '1px solid #2f2f35' : '1px solid #e5e7eb',
          color: theme === 'dark' ? '#fafafa' : '#18181b',
        },
      }}
    />
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <ToasterWithTheme />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
